// businessCache — iter118.
//
// Tiny localStorage write-through cache for the business + vendor list
// endpoints so the Discover/Map/Tonight pages survive offline.
//
// Why not the Service Worker?
// The SW serves /api responses cache-first only for the explicit
// "Save App for Offline" download — and even then, only the precise
// URLs it pre-fetched. Filter combinations (category=cigar_lounge,
// neighborhood=Vedado, etc.) hit fresh URLs the SW has never seen.
// This module gives us a forgiving fallback: ALL businesses cached as
// one flat list, every call falls back to the cached set when the
// network is dead — even if the active filters are new.
//
// Public shape:
//   • cachedBusinessGet(path)  → Promise<{ data, fromCache }>
//   • readCache(key)            → array | null
//   • writeCache(key, rows)     → void
//
// The helpers are intentionally synchronous-ish (apart from the
// network round-trip) so call sites stay flat.
import { useEffect, useState } from "react";
import { api } from "./api";

const KEY = {
  businesses: "habano.cache.businesses",
  vendors: "habano.cache.vendors",
  tonight: "habano.cache.tonight",
  map: "habano.cache.map.businesses",
};

function safeRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

function safeWrite(key, rows) {
  try {
    if (!Array.isArray(rows)) return;
    localStorage.setItem(key, JSON.stringify(rows));
  } catch { /* quota / disabled — ignore */ }
}

export function readCachedBusinesses() { return safeRead(KEY.businesses); }
export function readCachedVendors()    { return safeRead(KEY.vendors); }
export function readCachedTonight()    { return safeRead(KEY.tonight); }

export function writeCachedBusinesses(rows) { safeWrite(KEY.businesses, rows); }
export function writeCachedVendors(rows)    { safeWrite(KEY.vendors, rows); }
export function writeCachedTonight(rows)    { safeWrite(KEY.tonight, rows); }

/**
 * Fetch businesses from the API with a localStorage write-through.
 *
 *   • Online → fetch, cache the result, return it.
 *   • Offline (or any fetch failure) → resolve from cache (if any).
 *   • No cache + offline → resolve with [] so callers never crash.
 *
 * `kind` chooses the cache slot. `path` is the full /api path
 * (without /api prefix — handled by axios baseURL).
 */
export async function cachedBusinessGet(path, kind = "businesses") {
  const cacheKey = KEY[kind] || KEY.businesses;
  // iter122 — Short-circuit before the network call when the device
  // reports offline, so iOS never raises its native "Cellular Data is
  // Turned Off" dialog. Cached value (or [] when empty) wins.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { data: safeRead(cacheKey) || [], fromCache: true };
  }
  try {
    const { data } = await api.get(path);
    const rows = Array.isArray(data) ? data : [];
    safeWrite(cacheKey, rows);
    return { data: rows, fromCache: false };
  } catch {
    const cached = safeRead(cacheKey) || [];
    return { data: cached, fromCache: true };
  }
}

/**
 * Apply category/neighborhood-style filters CLIENT-side against the
 * full cached business list. Used as a fallback path when the
 * server-side filter URL hasn't been cached.
 */
export function applyClientBusinessFilters(rows, { category, neighborhood, search } = {}) {
  let out = Array.isArray(rows) ? rows.slice() : [];
  if (category) out = out.filter((b) => b.category === category);
  if (neighborhood) out = out.filter((b) => b.neighborhood === neighborhood);
  if (search) {
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((b) => (b.name || "").toLowerCase().includes(q)
      || (b.description || "").toLowerCase().includes(q));
  }
  return out;
}

/**
 * iter122 — Drop-in hook used by every page that lists businesses.
 *
 *   const { businesses, isOffline, isLoading, fromCache } = useCachedBusinesses();
 *
 * On mount it synchronously returns whatever is cached so the UI is
 * never blank, then attempts a refresh in the background. Offline /
 * fetch-failure paths simply stop without bubbling errors.
 */
export function useCachedBusinesses(path = "/businesses?limit=200") {
  const [businesses, setBusinesses] = useState(() => readCachedBusinesses() || []);
  const [isLoading, setIsLoading] = useState(true);
  const [fromCache, setFromCache] = useState(true);
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);

  useEffect(() => {
    let cancelled = false;
    cachedBusinessGet(path, "businesses").then(({ data, fromCache: fc }) => {
      if (cancelled) return;
      setBusinesses(Array.isArray(data) ? data : []);
      setFromCache(fc);
      setIsOffline(typeof navigator !== "undefined" && navigator.onLine === false);
      setIsLoading(false);
    });
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [path]);

  return { businesses, isLoading, isOffline, fromCache };
}

/** iter122 — Lookup a single cached business by id. Used as a fallback
 *  in PlaceProfile when /api/businesses/{id} fails offline. */
export function findCachedBusinessById(id) {
  const list = readCachedBusinesses() || [];
  return list.find((b) => b && b.id === id) || null;
}

/** iter122 — Safe single-business GET. Returns cached row when offline so
 *  iOS never raises the "Cellular Data is Turned Off" dialog. Falls back
 *  to cache on any API failure. Resolves with `null` when no data is
 *  available — callers handle that gracefully by skipping rendering. */
export async function safeBusinessByIdGet(id) {
  const cached = findCachedBusinessById(id);
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return cached;
  }
  try {
    const { data } = await api.get(`/businesses/${id}`);
    return data || cached;
  } catch {
    return cached;
  }
}

/** iter122 — Fire-and-forget cache warmer. Call once on app mount; it
 *  reads-through the businesses + tonight endpoints and stores them
 *  to localStorage so subsequent offline visits have data to render. */
let _warmedThisSession = false;
export function warmGlobalCache() {
  if (_warmedThisSession) return;
  _warmedThisSession = true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  cachedBusinessGet("/businesses?limit=200", "businesses").catch(() => {});
  cachedBusinessGet("/businesses?sort=tonight&limit=24", "tonight").catch(() => {});
}
